import React, { useState, useEffect } from "react";
import { X, Loader2 } from "lucide-react";

export default function OneOffPurchaseModal({ isOpen, onClose, vendors, loading, onSubmit }) {
  const [poNumber, setPoNumber] = useState("");
  const [vendor, setVendor] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [vendorCost, setVendorCost] = useState("0.00");
  const [shippingCost, setShippingCost] = useState("0.00");
  const [estArrival, setEstArrival] = useState("");
  const [notes, setNotes] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const suggestions = vendors.filter(v =>
    v.name && v.name.toLowerCase().includes((vendor || "").toLowerCase())
  );

  useEffect(() => {
    if (isOpen) {
      setPoNumber(`ENT-${Math.floor(1000 + Math.random() * 9000)}`);
      setVendor("");
      setDescription("");
      setQuantity(1);
      setVendorCost("0.00");
      setShippingCost("0.00");
      setEstArrival("");
      setNotes("");
      setShowSuggestions(false);
    }
  }, [isOpen, vendors]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      poNumber,
      vendor,
      description,
      quantity: Number(quantity) || 0,
      vendorCost: Number(vendorCost) || 0,
      shippingCost: Number(shippingCost) || 0,
      estArrival,
      notes
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "550px", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="modal-header">
          <h3>One-Off Enterprise Purchase</h3>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ marginTop: "1rem" }}>
          
          <div className="grid-2" style={{ gap: "1rem", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            {/* PO Number */}
            <div className="form-group">
              <label className="form-label" htmlFor="poNumber">PO Number *</label>
              <input
                id="poNumber"
                type="text"
                required
                className="form-input"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
              />
            </div>

            {/* Vendor Search Input */}
            <div className="form-group" style={{ position: "relative" }}>
              <label className="form-label" htmlFor="vendor">Vendor *</label>
              <input
                id="vendor"
                type="text"
                required
                className="form-input"
                value={vendor}
                onChange={(e) => {
                  setVendor(e.target.value);
                  setShowSuggestions(true);
                }}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => {
                  // Blur delay to let option clicks resolve first
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="Type to search/add vendor..."
                autoComplete="off"
              />
              {showSuggestions && vendor.trim() && (
                <div style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "var(--bg-card)",
                  border: "1px solid var(--border-color)",
                  borderRadius: "4px",
                  maxHeight: "150px",
                  overflowY: "auto",
                  zIndex: 200,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
                  marginTop: "2px"
                }}>
                  {suggestions.length === 0 ? (
                    <div style={{ padding: "0.5rem 0.75rem", fontSize: "0.85rem", color: "var(--text-muted)", fontStyle: "italic" }}>
                      Create custom vendor: "{vendor}"
                    </div>
                  ) : (
                    suggestions.map(v => (
                      <div
                        key={v.id}
                        onClick={() => {
                          setVendor(v.name);
                          setShowSuggestions(false);
                        }}
                        style={{
                          padding: "0.5rem 0.75rem",
                          fontSize: "0.85rem",
                          cursor: "pointer",
                          borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                          color: "var(--text-primary)"
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = "rgba(255, 255, 255, 0.05)"}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = "transparent"}
                      >
                        {v.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Item Description */}
          <div className="form-group">
            <label className="form-label" htmlFor="description">Item Description *</label>
            <textarea
              id="description"
              required
              className="form-input"
              placeholder="e.g. Office Supplies, Warehouse Tooling..."
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              style={{
                resize: "none",
                overflowY: "hidden",
                minHeight: "38px",
                wordBreak: "break-word",
                whiteSpace: "pre-wrap",
                lineHeight: "1.4",
                padding: "0.5rem 0.75rem"
              }}
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = `${el.scrollHeight}px`;
                }
              }}
            />
          </div>

          <div className="grid-3" style={{ gap: "0.75rem", display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
            {/* Quantity */}
            <div className="form-group">
              <label className="form-label" htmlFor="quantity">Quantity *</label>
              <input
                id="quantity"
                type="number"
                min="1"
                required
                className="form-input"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>

            {/* Vendor Cost */}
            <div className="form-group">
              <label className="form-label" htmlFor="vendorCost">Unit Cost ($) *</label>
              <input
                id="vendorCost"
                type="number"
                step="0.01"
                min="0"
                required
                className="form-input"
                value={vendorCost}
                onChange={(e) => setVendorCost(e.target.value)}
                onBlur={() => setVendorCost(Number(vendorCost).toFixed(2))}
              />
            </div>

            {/* Shipping Cost */}
            <div className="form-group">
              <label className="form-label" htmlFor="shippingCost">Shipping Cost ($)</label>
              <input
                id="shippingCost"
                type="number"
                step="0.01"
                min="0"
                className="form-input"
                value={shippingCost}
                onChange={(e) => setShippingCost(e.target.value)}
                onBlur={() => setShippingCost(Number(shippingCost).toFixed(2))}
              />
            </div>
          </div>

          {/* Est Arrival Date */}
          <div className="form-group">
            <label className="form-label" htmlFor="estArrival">Estimated Arrival Date</label>
            <input
              id="estArrival"
              type="date"
              className="form-input"
              value={estArrival}
              onChange={(e) => setEstArrival(e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label className="form-label" htmlFor="notes">Notes / Logs</label>
            <textarea
              id="notes"
              className="form-input"
              placeholder="Any additional purchase logs..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ minHeight: "80px", resize: "vertical" }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1.5rem" }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <Loader2 className="spinner-icon" size={16} /> : null}
              Log Purchase
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
